import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  buildTranscriptSummary,
  fetchRecordingAudioObjectUrl
} from '../../services/transcriptionService';

const formatRecordingDateLabel = (value) => {
  if (!value) return 'Unknown date';
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return 'Unknown date';
  return parsed.toLocaleDateString([], {
    weekday: 'short',
    day: '2-digit',
    month: 'short',
    year: 'numeric'
  });
};

const formatRecordingTimeLabel = (value) => {
  if (!value) return '';
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return '';
  return parsed.toLocaleTimeString([], {
    hour: '2-digit',
    minute: '2-digit'
  });
};

const isVoicemailRecording = (item = {}) => (
  String(item?.CallTypeGroup || item?.callTypeGroup || '').toLowerCase() === 'voicemail' ||
  String(item?.CallType || item?.callType || '').toLowerCase().includes('voicemail') ||
  Boolean(item?.IsVoicemail || item?.isVoicemail || item?.Voicemail || item?.voicemail) ||
  (Array.isArray(item?.Voicemails) && item.Voicemails.length > 0) ||
  (Array.isArray(item?.voicemails) && item.voicemails.length > 0) ||
  Number(item?.VoicemailCount || item?.voicemailCount || 0) > 0 ||
  Boolean(item?.VoicemailAt || item?.voicemailAt || item?.VoicemailMarker || item?.voicemailMarker) ||
  String(item?.Source || item?.source || '').toLowerCase().includes('voicemail')
);

const getRecordingAgentLabel = (item = {}) => {
  const agentLabel = (
    item?.AnsweredBy ||
    item?.answeredBy ||
    item?.QueueAgent ||
    item?.queueAgent ||
    item?.AgentExtension ||
    item?.agentExtension ||
    item?.UserId ||
    item?.userId ||
    'Unassigned'
  );

  return agentLabel === 'Unassigned' && isVoicemailRecording(item) ? 'Voicemail' : agentLabel;
};

const getRecordingTypeLabel = (item = {}) => {
  const explicitLabel = item?.CallTypeLabel || item?.callTypeLabel;
  if (explicitLabel) return explicitLabel;

  const explicitType = String(item?.CallType || item?.callType || '').trim().toLowerCase();
  if (explicitType === 'queue_voicemail') return 'Queue voicemail';
  if (explicitType === 'after_hours_voicemail') return 'After-hours voicemail';
  if (explicitType === 'agent_call') return 'Agent call';

  return isVoicemailRecording(item) ? 'Voicemail' : 'Agent call';
};

const getRecordingCallerLabel = (item = {}) =>
  item?.CallerNumber || item?.callerNumber || item?.CallerName || item?.callerName || 'Unknown caller';

const getRecordingPlaybackKey = (item = {}) =>
  item?.recordingKey || item?.recordingName || item?.RecordingName || item?.UniqueId || item?.uniqueId || '';

const getBubbleTone = (entry = {}, align = 'start') => {
  if (align === 'center' || entry?.speaker === 'System') return 'system';
  if (entry?.speaker === 'Agent') return 'agent';
  if (entry?.speaker === 'Caller') return 'caller';
  return align === 'end' ? 'agent' : 'neutral';
};

const RightPanelNew = ({
  transcript,
  isLive,
  onTextInsert,
  transcriptStatus = null,
  recordings = [],
  recordingsLoading = false,
  recordingsError = '',
  recordingFilterDraft = {},
  onRecordingFilterDraftChange,
  onRecordingFiltersApply,
  isRecordingFilterDirty = false,
  onRecordingSelect,
  selectedRecordingKey = '',
  speakerRoster = [],
  agentSpeakerId = '',
  onAgentSpeakerChange,
  isCallActive = false
}) => {
  void onTextInsert;

  const [isRecordingsOpen, setIsRecordingsOpen] = useState(false);
  const [isSummaryOpen, setIsSummaryOpen] = useState(false);
  const [isStatusNoteVisible, setIsStatusNoteVisible] = useState(false);
  const [activeSearchTerm, setActiveSearchTerm] = useState('');
  const [recordingTranscriptOutput, setRecordingTranscriptOutput] = useState('original');
  const audioRef = useRef(null);
  const audioUrlRef = useRef('');
  const [recordingPlayback, setRecordingPlayback] = useState({
    key: '',
    status: 'idle',
    error: ''
  });

  const entries = useMemo(() => (Array.isArray(transcript) ? transcript : []), [transcript]);
  const list = useMemo(() => (Array.isArray(recordings) ? recordings : []), [recordings]);
  const statusState = String(
    transcriptStatus?.state || (isLive ? 'live' : 'ended')
  ).toLowerCase();

  useEffect(() => {
    if (!isRecordingsOpen) return undefined;

    const previousOverflow = document.body.style.overflow;
    const handleKeyDown = (event) => {
      if (event.key === 'Escape') {
        setIsRecordingsOpen(false);
      }
    };

    document.body.style.overflow = 'hidden';
    window.addEventListener('keydown', handleKeyDown);

    return () => {
      document.body.style.overflow = previousOverflow;
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [isRecordingsOpen]);

  useEffect(() => () => {
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current.src = '';
      audioRef.current = null;
    }
    if (audioUrlRef.current) {
      URL.revokeObjectURL(audioUrlRef.current);
      audioUrlRef.current = '';
    }
  }, []);

  useEffect(() => {
    if (!isCallActive || !audioRef.current || audioRef.current.paused) return;

    audioRef.current.pause();
    setRecordingPlayback((prev) => (
      prev.key && (prev.status === 'playing' || prev.status === 'loading')
        ? { ...prev, status: 'paused', error: '' }
        : prev
    ));
  }, [isCallActive]);

  const statusLabel = (() => {
    if (statusState === 'completed') return 'Ready';
    if (statusState === 'processing' || statusState === 'pending') return 'Processing';
    if (statusState === 'failed') return 'Failed';
    return isLive ? 'Live' : 'Ended';
  })();

  const statusBadgeClass = (() => {
    if (statusState === 'completed') return 'bg-info';
    if (statusState === 'processing' || statusState === 'pending' || isLive) return 'bg-success';
    if (statusState === 'failed') return 'bg-danger';
    return 'bg-secondary';
  })();

  const statusNoteClass = (() => {
    if (statusState === 'failed') return 'transcript-status-note transcript-status-note-failed';
    if (statusState === 'processing' || statusState === 'pending') {
      return 'transcript-status-note transcript-status-note-processing';
    }
    if (statusState === 'completed') return 'transcript-status-note transcript-status-note-ready';
    return 'transcript-status-note';
  })();

  const statusMessage = String(transcriptStatus?.message || '').trim();
  const isCompletedWithoutEntries = statusState === 'completed' && entries.length === 0;

  useEffect(() => {
    if (!statusMessage) {
      setIsStatusNoteVisible(false);
      return undefined;
    }

    setIsStatusNoteVisible(true);

    if (statusState !== 'completed') {
      return undefined;
    }

    const timeoutId = window.setTimeout(() => {
      setIsStatusNoteVisible(false);
    }, 3600);

    return () => {
      window.clearTimeout(timeoutId);
    };
  }, [statusMessage, statusState]);

  const copyPayload = entries
    .map((entry) => {
      const speakerLabel = entry?.speakerLabel || entry?.speaker || 'Transcript';
      const timestamp = entry?.timestamp ? ` (${entry.timestamp})` : '';
      return `${speakerLabel}${timestamp}: ${entry?.text || ''}`.trim();
    })
    .join('\n');
  const summary = useMemo(() => buildTranscriptSummary(entries), [entries]);
  const normalizedSearchTerm = activeSearchTerm.trim().toLowerCase();
  const matchingEntryIds = useMemo(() => {
    if (!normalizedSearchTerm) {
      return new Set();
    }

    return new Set(
      entries
        .filter((entry) => String(entry?.text || '').toLowerCase().includes(normalizedSearchTerm))
        .map((entry, index) => entry?.id || `entry-${index + 1}`)
    );
  }, [entries, normalizedSearchTerm]);
  const searchMatchCount = matchingEntryIds.size;

  const groupedRecordings = useMemo(() => {
    // Robustly parse timestamps (Postgres returns "2026-06-18 13:52:22.000000" which
    // Date.parse rejects), so the newest recording always sorts to the top regardless
    // of type (agent / after-hours VM / queue VM).
    const getRecordingTime = (item) => {
      const raw = String(
        item?.CallEndedAt ||
        item?.callEndedAt ||
        item?.CallStartedAt ||
        item?.callStartedAt ||
        item?.RecordingStoppedAt ||
        item?.recordingStoppedAt ||
        item?.RecordingStartedAt ||
        item?.recordingStartedAt ||
        item?.CreatedAt ||
        item?.createdAt ||
        ''
      ).trim();
      if (!raw) return 0;
      let ts = Date.parse(raw);
      if (Number.isNaN(ts)) ts = Date.parse(raw.replace(' ', 'T'));
      if (Number.isNaN(ts)) ts = Date.parse(raw.replace(' ', 'T').replace(/\.\d+/, ''));
      return Number.isNaN(ts) ? 0 : ts;
    };

    return [...list]
      .sort((a, b) => getRecordingTime(b) - getRecordingTime(a))
      .reduce((groups, item) => {
        const dateValue =
          item?.CallStartedAt ||
          item?.callStartedAt ||
          item?.CallEndedAt ||
          item?.callEndedAt ||
          item?.CreatedAt ||
          item?.createdAt ||
          '';
        const groupLabel = formatRecordingDateLabel(dateValue);
        if (!groups[groupLabel]) {
          groups[groupLabel] = [];
        }
        groups[groupLabel].push(item);
        return groups;
      }, {});
  }, [list]);

  const recordingsCountLabel = `${list.length} ${list.length === 1 ? 'item' : 'items'}`;
  const filterSummary = [
    recordingFilterDraft?.from ? `From ${recordingFilterDraft.from}` : '',
    recordingFilterDraft?.to ? `To ${recordingFilterDraft.to}` : '',
    recordingFilterDraft?.callerNumber ? `Caller ${recordingFilterDraft.callerNumber}` : '',
    recordingFilterDraft?.recordingType && recordingFilterDraft.recordingType !== 'all'
      ? `Type ${
          {
            agent_call: 'Agent call',
            after_hours_voicemail: 'After-hours voicemail',
            queue_voicemail: 'Queue voicemail',
            voicemail: 'Voicemail'
          }[recordingFilterDraft.recordingType] || 'Agent call'
        }`
      : ''
  ].filter(Boolean).join(' | ');

  const handleRecordingCardClick = (item) => {
    onRecordingSelect?.(item, recordingTranscriptOutput);
    setIsRecordingsOpen(false);
  };

  const stopRecordingPlayback = () => {
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current.currentTime = 0;
    }
    setRecordingPlayback((prev) => ({
      key: prev.key,
      status: prev.key ? 'stopped' : 'idle',
      error: ''
    }));
  };

  const handleRecordingPlayback = async (event, item, action) => {
    event.stopPropagation();
    event.preventDefault();

    const playbackKey = getRecordingPlaybackKey(item);
    if (!playbackKey) return;

    if (action === 'stop') {
      stopRecordingPlayback();
      return;
    }

    if (action === 'pause') {
      if (audioRef.current && recordingPlayback.key === playbackKey) {
        audioRef.current.pause();
      }
      setRecordingPlayback({ key: playbackKey, status: 'paused', error: '' });
      return;
    }

    try {
      setRecordingPlayback({ key: playbackKey, status: 'loading', error: '' });

      if (!audioRef.current || recordingPlayback.key !== playbackKey) {
        if (audioRef.current) {
          audioRef.current.pause();
          audioRef.current.src = '';
        }
        if (audioUrlRef.current) {
          URL.revokeObjectURL(audioUrlRef.current);
          audioUrlRef.current = '';
        }

        const objectUrl = await fetchRecordingAudioObjectUrl(item);
        const audio = new Audio(objectUrl);
        audioUrlRef.current = objectUrl;
        audioRef.current = audio;
        audio.addEventListener('ended', () => {
          setRecordingPlayback({ key: playbackKey, status: 'stopped', error: '' });
        });
      }

      await audioRef.current.play();
      setRecordingPlayback({ key: playbackKey, status: 'playing', error: '' });
    } catch (error) {
      setRecordingPlayback({
        key: playbackKey,
        status: 'failed',
        error: error.message || 'Recording could not be played.'
      });
    }
  };

  const handleExportTranscript = () => {
    if (!copyPayload) return;

    const filename = `transcript-${new Date().toISOString().replace(/[:.]/g, '-')}.txt`;
    const blob = new Blob([copyPayload], { type: 'text/plain;charset=utf-8' });
    const objectUrl = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = objectUrl;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.setTimeout(() => URL.revokeObjectURL(objectUrl), 1000);
  };

  const handleSearchClick = () => {
    const value = window.prompt('Search transcript', activeSearchTerm);
    if (value === null) return;
    setActiveSearchTerm(String(value || '').trim());
  };

  return (
    <>
      <div className="card h-100 shadow-sm border-0 d-flex flex-column transcript-panel" style={{ minHeight: 0 }}>
        <div className="card-header bg-white border-0 transcript-panel-header">
          <div className="d-flex justify-content-between align-items-start flex-wrap gap-3">
            <div className="transcript-panel-heading">
              <div className="d-flex align-items-center gap-2 mb-1">
                <h5 className="card-title mb-0 fw-bold text-dark">Live Transcript</h5>
                <span className={`badge rounded-pill ${statusBadgeClass}`}>
                  {statusLabel}
                </span>
              </div>
              <p className="transcript-panel-subtitle mb-0">
                Review transcripts, open saved call recordings, and adjust speaker layout when needed.
              </p>
            </div>

            <div className="btn-toolbar transcript-panel-toolbar gap-2 justify-content-end" role="toolbar">
              <div className="transcript-toolbar-actions" role="group" aria-label="Transcript actions">
                <button
                  type="button"
                  className="btn btn-outline-secondary btn-sm"
                  disabled={!entries.length}
                  onClick={() => navigator.clipboard.writeText(copyPayload)}
                >
                  <i className="bi bi-clipboard me-1"></i>
                  Copy All
                </button>
                <button
                  type="button"
                  className="btn btn-outline-secondary btn-sm"
                  disabled={!entries.length}
                  onClick={handleExportTranscript}
                >
                  <i className="bi bi-download me-1"></i>
                  Export
                </button>
                <button
                  type="button"
                  className="btn btn-outline-secondary btn-sm"
                  disabled={!entries.length}
                  onClick={handleSearchClick}
                  title={normalizedSearchTerm ? `Matches: ${searchMatchCount}` : 'Search transcript'}
                >
                  <i className="bi bi-search me-1"></i>
                  Search
                </button>
                <button
                  type="button"
                  className="btn btn-outline-info btn-sm"
                  disabled={!entries.length}
                  onClick={() => setIsSummaryOpen(true)}
                >
                  <i className="bi bi-stars me-1"></i>
                  Summary
                </button>
              </div>
            </div>
          </div>

        </div>

        <div className="card-body p-0 d-flex flex-column transcript-panel-body" style={{ minHeight: 0, flex: '1 1 0' }}>
          <section
            className={`transcript-control-row ${speakerRoster.length > 1 ? '' : 'transcript-control-row-single'}`}
          >
            <button
              type="button"
              className="transcript-recordings-launch"
              onClick={() => setIsRecordingsOpen(true)}
            >
              <div className="transcript-recordings-launch-copy">
                <span className="transcript-recordings-launch-title">
                  <i className="bi bi-collection-play transcript-recordings-launch-icon"></i>
                  Recordings
                </span>
              </div>
              <div className="transcript-recordings-launch-meta">
                <span className="transcript-count-pill">{recordingsCountLabel}</span>
                <span className="transcript-recordings-launch-link">
                  Open
                  <i className="bi bi-arrow-up-right-circle"></i>
                </span>
              </div>
            </button>

            {speakerRoster.length > 1 && (
              <div className="transcript-speaker-inline">
                <label className="transcript-speaker-label" htmlFor="transcript-agent-speaker-select">
                  Speaker layout
                </label>
                <select
                  id="transcript-agent-speaker-select"
                  className="form-select form-select-sm"
                  value={agentSpeakerId || ''}
                  onChange={(event) => onAgentSpeakerChange?.(event.target.value)}
                  title="Swap speakers if the agent and caller appear on the wrong sides."
                >
                  <option value="">Use detected speakers</option>
                  {speakerRoster.map((speaker) => (
                    <option key={speaker.speakerId} value={speaker.speakerId}>
                      {speaker.label} is the agent (right side)
                    </option>
                  ))}
                </select>
              </div>
            )}
          </section>

          {statusMessage && isStatusNoteVisible && (
            <div className="transcript-status-inline">
              <div className={statusNoteClass}>
                <i className="bi bi-info-circle transcript-status-note-icon"></i>
                <span>{statusMessage}</span>
              </div>
            </div>
          )}

          {normalizedSearchTerm && entries.length > 0 && (
            <div className="transcript-search-inline">
              <div className="transcript-search-note">
                <span>
                  {searchMatchCount} match{searchMatchCount === 1 ? '' : 'es'} for "{activeSearchTerm}"
                </span>
                <button
                  type="button"
                  className="btn btn-link btn-sm p-0 text-decoration-none"
                  onClick={() => setActiveSearchTerm('')}
                >
                  Clear
                </button>
              </div>
            </div>
          )}

          <section className="transcript-feed-section transcript-feed-scroll" style={{ minHeight: 0, flex: '1 1 0' }}>
            {entries.length === 0 ? (
              <div className="transcript-empty-state">
                <i
                  className={`display-4 mb-3 ${
                    statusState === 'failed'
                      ? 'bi bi-exclamation-triangle text-danger'
                      : (statusState === 'processing' || statusState === 'pending')
                        ? 'bi bi-hourglass-split text-info'
                        : 'bi bi-mic text-muted'
                  }`}
                ></i>
                <h6 className="text-muted fw-semibold">
                  {statusState === 'failed'
                    ? 'Transcript unavailable'
                    : (statusState === 'processing' || statusState === 'pending')
                      ? 'Transcript in progress'
                      : isCompletedWithoutEntries
                        ? 'No clear speech detected'
                      : isLive
                        ? 'Listening for speech...'
                        : 'No transcript available'}
                </h6>
                <small className="text-muted">
                  {statusState === 'failed'
                    ? (statusMessage || 'The recording could not be found or transcribed.')
                    : (statusState === 'processing' || statusState === 'pending')
                      ? 'The selected or most recent call recording is being processed.'
                      : isCompletedWithoutEntries
                        ? (statusMessage || 'Only background noise or unclear audio was detected.')
                      : isLive
                        ? 'The transcript will appear here automatically after the call ends.'
                        : 'Select a recording or wait for the latest completed call to appear here.'}
                </small>
              </div>
            ) : (
              <div className="transcript-feed">
                {entries.map((entry, index) => {
                  const speakerLabel = entry?.speakerLabel || entry?.speaker || 'Transcript';
                  const align = entry?.align || (
                    entry?.speaker === 'Agent'
                      ? 'end'
                      : entry?.speaker === 'System'
                        ? 'center'
                        : 'start'
                  );
                  const bubbleTone = getBubbleTone(entry, align);

                  return (
                    <div
                      key={entry?.id || index}
                      className={`transcript-feed-row transcript-feed-row-${align}`}
                    >
                      <div
                        className={`transcript-message transcript-message-${bubbleTone} ${
                          matchingEntryIds.has(entry?.id || `entry-${index + 1}`) ? 'is-search-match' : ''
                        }`}
                      >
                        <div className="transcript-message-label">{speakerLabel}</div>
                        <p className="transcript-message-text">{entry?.text}</p>
                        <div className={`transcript-message-meta transcript-message-meta-${align}`}>
                          {speakerLabel}
                          {entry?.timestamp ? ` at ${entry.timestamp}` : ''}
                        </div>
                      </div>
                    </div>
                  );
                })}
                {!isLive && entries.length > 0 && (
                  <div className="text-center">
                    <small className="text-muted">Call ended</small>
                  </div>
                )}
              </div>
            )}
          </section>
        </div>

        {(isLive || statusState === 'processing' || statusState === 'pending') && (
          <div className="transcript-panel-footer">
            <div className="transcript-footer-indicators">
              <div className="transcript-indicator transcript-indicator-recording">
                <span className="spinner-grow spinner-grow-sm" style={{ width: '0.5rem', height: '0.5rem' }}></span>
                <small>{isLive ? 'Recording active' : 'Recording captured'}</small>
              </div>
              <div className="transcript-indicator transcript-indicator-processing">
                <span className="spinner-grow spinner-grow-sm" style={{ width: '0.5rem', height: '0.5rem' }}></span>
                <small>
                  {statusState === 'processing' || statusState === 'pending'
                    ? 'Transcription running'
                    : isLive
                      ? 'Waiting for completion'
                      : 'Queued'}
                </small>
              </div>
            </div>
          </div>
        )}
      </div>

      {isSummaryOpen && (
        <div className="transcript-modal-backdrop" onClick={() => setIsSummaryOpen(false)}>
          <div
            className="transcript-modal-shell transcript-summary-modal"
            role="dialog"
            aria-modal="true"
            aria-labelledby="transcript-summary-modal-title"
            onClick={(event) => event.stopPropagation()}
          >
            <div className="transcript-modal-header">
              <div className="transcript-modal-copy">
                <div className="transcript-section-kicker">Summary</div>
                <h5 className="mb-1 text-dark" id="transcript-summary-modal-title">Conversation summary</h5>
                <p className="transcript-modal-subtitle mb-0">
                  Main points discussed between the caller and the agent.
                </p>
              </div>
              <div className="transcript-modal-header-actions">
                <button
                  type="button"
                  className="btn btn-outline-secondary btn-sm transcript-modal-close"
                  onClick={() => setIsSummaryOpen(false)}
                  aria-label="Close summary"
                >
                  <i className="bi bi-x-lg"></i>
                </button>
              </div>
            </div>

            <div className="transcript-modal-body">
              {!summary.hasContent ? (
                <div className="transcript-inline-state">A summary will appear once transcript text is available.</div>
              ) : (
                <div className="transcript-summary-grid">
                  {summary.overview && (
                    <section className="transcript-summary-card">
                      <div className="transcript-summary-heading">Overview</div>
                      <p className="transcript-summary-overview mb-0">{summary.overview}</p>
                    </section>
                  )}

                  {summary.callerHighlights.length > 0 && (
                    <section className="transcript-summary-card">
                      <div className="transcript-summary-heading">Caller points</div>
                      <div className="transcript-summary-list">
                        {summary.callerHighlights.map((item, index) => (
                          <div key={`caller-${index + 1}`} className="transcript-summary-item">
                            {item}
                          </div>
                        ))}
                      </div>
                    </section>
                  )}

                  {summary.agentHighlights.length > 0 && (
                    <section className="transcript-summary-card">
                      <div className="transcript-summary-heading">Agent points</div>
                      <div className="transcript-summary-list">
                        {summary.agentHighlights.map((item, index) => (
                          <div key={`agent-${index + 1}`} className="transcript-summary-item">
                            {item}
                          </div>
                        ))}
                      </div>
                    </section>
                  )}

                  {summary.keyMoments.length > 0 && (
                    <section className="transcript-summary-card">
                      <div className="transcript-summary-heading">Key moments</div>
                      <div className="transcript-summary-list">
                        {summary.keyMoments.map((item) => (
                          <div key={item.id} className="transcript-summary-item">
                            <strong>{item.speaker}:</strong> {item.text}
                            {item.timestamp ? ` (${item.timestamp})` : ''}
                          </div>
                        ))}
                      </div>
                    </section>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {isRecordingsOpen && (
        <div className="transcript-modal-backdrop" onClick={() => setIsRecordingsOpen(false)}>
          <div
            className="transcript-modal-shell transcript-recordings-modal"
            role="dialog"
            aria-modal="true"
            aria-labelledby="transcript-recordings-modal-title"
            onClick={(event) => event.stopPropagation()}
          >
            <div className="transcript-modal-header">
              <div className="transcript-modal-copy">
                <div className="transcript-section-kicker">Recordings</div>
                <h5 className="mb-1 text-dark" id="transcript-recordings-modal-title">Agent recordings</h5>
                <p className="transcript-modal-subtitle mb-0">
                  Choose a date range, press Apply, then select any saved call to load its transcript.
                </p>
              </div>
              <div className="transcript-modal-header-actions">
                <span className="transcript-count-pill">{recordingsCountLabel}</span>
                <button
                  type="button"
                  className="btn btn-outline-secondary btn-sm transcript-modal-close"
                  onClick={() => setIsRecordingsOpen(false)}
                  aria-label="Close recordings"
                >
                  <i className="bi bi-x-lg"></i>
                </button>
              </div>
            </div>

            <div className="transcript-modal-body">
              <div className="transcript-filter-grid transcript-filter-grid-modal">
                <div className="transcript-filter-field">
                  <label className="form-label mb-1">From</label>
                  <input
                    type="date"
                    className="form-control form-control-sm"
                    value={recordingFilterDraft?.from || ''}
                    onChange={(event) => onRecordingFilterDraftChange?.('from', event.target.value)}
                  />
                </div>
                <div className="transcript-filter-field">
                  <label className="form-label mb-1">To</label>
                  <input
                    type="date"
                    className="form-control form-control-sm"
                    value={recordingFilterDraft?.to || ''}
                    onChange={(event) => onRecordingFilterDraftChange?.('to', event.target.value)}
                  />
                </div>
                <div className="transcript-filter-field">
                  <label className="form-label mb-1">Caller Phone</label>
                  <input
                    type="search"
                    className="form-control form-control-sm"
                    value={recordingFilterDraft?.callerNumber || ''}
                    onChange={(event) => onRecordingFilterDraftChange?.('callerNumber', event.target.value)}
                    placeholder="0345..."
                  />
                </div>
                <div className="transcript-filter-field">
                  <label className="form-label mb-1">Recording Type</label>
                  <div className="transcript-recording-type-toggle" role="group" aria-label="Recording type">
                    {[
                      ['all', 'All'],
                      ['agent_call', 'Agent Call'],
                      ['after_hours_voicemail', 'After Hours VM'],
                      ['queue_voicemail', 'Queue VM'],
                      ['voicemail', 'All VM']
                    ].map(([value, label]) => (
                      <button
                        key={value}
                        type="button"
                        className={`transcript-recording-type-option ${
                          (recordingFilterDraft?.recordingType || 'all') === value ? 'is-active' : ''
                        }`}
                        onClick={() => onRecordingFilterDraftChange?.('recordingType', value)}
                      >
                        {label}
                      </button>
                    ))}
                  </div>
                </div>
                <div className="transcript-filter-field">
                  <label className="form-label mb-1" htmlFor="recording-transcript-output">
                    Transcript Output
                  </label>
                  <select
                    id="recording-transcript-output"
                    className="form-select form-select-sm"
                    value={recordingTranscriptOutput}
                    onChange={(event) => setRecordingTranscriptOutput(event.target.value)}
                  >
                    <option value="original">Original language</option>
                    <option value="en">English translation</option>
                  </select>
                </div>
              </div>

              <div className="transcript-filter-actions">
                <div className="transcript-filter-summary">
                  {filterSummary || 'Pick a date range to review saved recordings.'}
                </div>
                <button
                  type="button"
                  className={`btn btn-sm ${isRecordingFilterDirty ? 'btn-primary' : 'btn-outline-primary'}`}
                  onClick={() => onRecordingFiltersApply?.()}
                  disabled={recordingsLoading}
                >
                  {recordingsLoading ? 'Applying...' : 'Apply'}
                </button>
              </div>

              {recordingsError && (
                <div className="alert alert-warning py-2 mb-0 small">{recordingsError}</div>
              )}
              {recordingsLoading && (
                <div className="transcript-inline-state">Loading recordings...</div>
              )}
              {!recordingsLoading && !recordingsError && list.length === 0 && (
                <div className="transcript-inline-state">No recordings were found in this date range.</div>
              )}
              {!recordingsLoading && list.length > 0 && (
                <div className="transcript-recording-list transcript-recording-list-modal">
                  {Object.entries(groupedRecordings).map(([groupLabel, items]) => (
                    <div key={groupLabel} className="transcript-recording-group">
                      <div className="transcript-recording-group-label">{groupLabel}</div>
                      <div className="transcript-recording-stack">
                        {items.map((item) => {
                          const startedAt =
                            item?.CallStartedAt ||
                            item?.callStartedAt ||
                            item?.CallEndedAt ||
                            item?.callEndedAt ||
                            item?.CreatedAt ||
                            item?.createdAt ||
                            '';
                          const playbackKey = getRecordingPlaybackKey(item);
                          const isSelected = selectedRecordingKey && item?.recordingKey === selectedRecordingKey;
                          const isPlaybackActive = recordingPlayback.key === playbackKey;
                          const isPlaying = isPlaybackActive && recordingPlayback.status === 'playing';
                          const isPaused = isPlaybackActive && recordingPlayback.status === 'paused';
                          const isStopped = isPlaybackActive && recordingPlayback.status === 'stopped';
                          const isLoadingPlayback = isPlaybackActive && recordingPlayback.status === 'loading';

                          return (
                            <div
                              key={item?.recordingKey || item?.recordingName || item?.UniqueId || item?.uniqueId}
                              className={`transcript-recording-card ${isSelected ? 'is-selected' : ''}`}
                              role="button"
                              tabIndex={0}
                              onClick={() => handleRecordingCardClick(item)}
                              onKeyDown={(event) => {
                                if (event.key === 'Enter' || event.key === ' ') {
                                  event.preventDefault();
                                  handleRecordingCardClick(item);
                                }
                              }}
                            >
                              <div className="transcript-recording-card-top">
                                <span className="transcript-recording-time">
                                  {formatRecordingTimeLabel(startedAt) || 'Unknown time'}
                                </span>
                                <div className="transcript-recording-card-badges">
                                  <span className="transcript-recording-agent-pill transcript-recording-type-pill">
                                    {getRecordingTypeLabel(item)}
                                  </span>
                                  <span className="transcript-recording-agent-pill">
                                    {getRecordingAgentLabel(item)}
                                  </span>
                                </div>
                              </div>
                              <div className="transcript-recording-caller">
                                {getRecordingCallerLabel(item)}
                              </div>
                              <div className="transcript-recording-meta">
                                Type: {getRecordingTypeLabel(item)}
                              </div>
                              <div className="transcript-recording-file">
                                {item?.recordingName || item?.RecordingName || 'No recording file'}
                              </div>
                              {(item?.QueryId || item?.queryId) && (
                                <div className="transcript-recording-meta">
                                  Query ID: {item?.QueryId || item?.queryId}
                                </div>
                              )}
                              <div className="transcript-recording-playback">
                                <button
                                  type="button"
                                  className={`btn btn-sm btn-outline-primary ${isPlaying || isLoadingPlayback ? 'is-playback-playing' : ''}`}
                                  onClick={(event) => handleRecordingPlayback(event, item, 'play')}
                                  disabled={isCallActive || isLoadingPlayback}
                                  aria-pressed={isPlaying}
                                >
                                  <i className={`bi ${isLoadingPlayback ? 'bi-hourglass-split' : 'bi-play-fill'}`}></i>
                                  {isLoadingPlayback ? 'Loading' : 'Play'}
                                </button>
                                <button
                                  type="button"
                                  className={`btn btn-sm btn-outline-secondary ${isPaused ? 'is-playback-paused' : ''}`}
                                  onClick={(event) => handleRecordingPlayback(event, item, 'pause')}
                                  disabled={!isPlaying}
                                  aria-pressed={isPaused}
                                >
                                  <i className="bi bi-pause-fill"></i>
                                  Pause
                                </button>
                                <button
                                  type="button"
                                  className={`btn btn-sm btn-outline-danger ${isStopped ? 'is-playback-stopped' : ''}`}
                                  onClick={(event) => handleRecordingPlayback(event, item, 'stop')}
                                  disabled={!isPlaybackActive || recordingPlayback.status === 'idle'}
                                  aria-pressed={isStopped}
                                >
                                  <i className="bi bi-stop-fill"></i>
                                  Stop
                                </button>
                              </div>
                              {isPlaybackActive && recordingPlayback.error && (
                                <div className="transcript-recording-playback-error">
                                  {recordingPlayback.error}
                                </div>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default RightPanelNew;
